import create from 'zustand';
import { ReplacementsLibraryListItem } from '@replacements/components/ReplacementsLibrary/List/ReplacementsLibraryList';
import { insertToLibrary } from '@replacements/utils/aggregateReplacementsByWord';
import { ReplacementLibraryItem } from '@replacements/components/ReplacementsLibrary/Accordion/ReplacementsLibraryAccordion';
import { LibraryToolbarDrawerContentType } from '@replacements/types/replacementLibraryTypes';
import {
  PhoneticLibraryType,
  PhoneticReplacement,
} from '../../../../server/graphql/__generated.types';

/**
 * Represents persisted data along with
 * unpersisted properties only needed for rendering the UI state
 */
export interface ReplacementLibraryStoreItem
  extends ReplacementsLibraryListItem {
  /**
   * True if list item should render with accordion expanded
   */
  expanded: boolean;
  /**
   * Replacement text of replacement that should be selected
   */
  selectedReplacement?: string;
  /**
   * True if one of the replacements for this item is enabled
   */
  hasReplacementEnabled?: boolean;

  selected: boolean;
}

export type ReplacementsLibrary = Array<ReplacementLibraryStoreItem>;

export enum ReplacementTypeFilter {
  all = 'all',
  currentScript = 'current_script',
  personal = 'personal',
  team = 'team',
}

export type ExtendedPhoneticLibraryType = PhoneticLibraryType | 'all';

export interface ReplacementsLibraryStore {
  /**
   * TODO: add `activeType` and refactor state/actions to be scoped
   library: {
      personal: [],
      team: [],
      all: [],
      legacy: []
   }
  */
  activeType: ExtendedPhoneticLibraryType;
  shouldFocusReplacement: boolean;
  setShouldFocusReplacement: (shouldFocusReplacement: boolean) => void;
  defaultReplacementOriginalWord: string;
  setDefaultReplacementOriginalWord: (
    defaultReplacementOriginalWord: string
  ) => void;
  isNewReplacementSidebarAccordionOpen: boolean;
  setIsNewReplacementSidebarAccordionOpen: (isOpen: boolean) => void;
  library: ReplacementsLibrary;
  drawerContentType: LibraryToolbarDrawerContentType;
  setActiveType: (activeType: ExtendedPhoneticLibraryType) => void;
  initLibrary: (library: Array<ReplacementsLibraryListItem>) => void;
  expandWord: (id: string, expanded: boolean) => void;
  addWord: (phoneticReplacement: PhoneticReplacement) => void;
  /**
   * adds a replacement to the library as well as caller context to replacements
   * IE whether or not a single replacement is coming from a bulk context
   */
  addReplacement: (
    phoneticReplacement: PhoneticReplacement,
    context?: { type: 'single' | 'bulk' }
  ) => void;
  selectReplacement: (text: string, replacementText: string) => void;
  clearSelectedReplacement: () => void;
  selectWord: (text: string, deselect?: boolean) => void;
  getSelectedWord: () => string;
  getSelectedReplacement: () => ReplacementLibraryItem | undefined;
  updateDrawerContentType: (type: LibraryToolbarDrawerContentType) => void;
  deleteReplacement: (
    text: string,
    replacementId: string
  ) => LibraryToolbarDrawerContentType;
  updateReplacement: (
    phoneticReplacement: PhoneticReplacement,
    selectReplacement?: boolean
  ) => void;
  deleteWord: (text: string) => void;
  collapseWords: () => void;
}

function deepCopyArray<T>(array: T[]): T[] {
  return JSON.parse(JSON.stringify(array));
}

/**
 * Use store to read library entries from and maintain additional state
 * needed for UI
 * note: list on backend is sorted by order text asc and createdAt desc
 * but we don't care about createdAt in this context because - we will add latest ones at top of list
 */
export const useReplacementsLibraryStore = create<ReplacementsLibraryStore>(
  (set, get) => ({
    library: [],
    activeType: PhoneticLibraryType.Personal,
    shouldFocusReplacement: false,
    setShouldFocusReplacement: (shouldFocusReplacement: boolean) => {
      set(() => {
        return { shouldFocusReplacement };
      });
    },
    defaultReplacementOriginalWord: '',
    setDefaultReplacementOriginalWord: (
      defaultReplacementOriginalWord: string
    ) => {
      set(() => {
        return { defaultReplacementOriginalWord };
      });
    },
    isNewReplacementSidebarAccordionOpen: false,
    setIsNewReplacementSidebarAccordionOpen: (isOpen: boolean) => {
      set(() => {
        return { isNewReplacementSidebarAccordionOpen: isOpen };
      });
    },
    drawerContentType: LibraryToolbarDrawerContentType.Default,
    initLibrary: (library: Array<ReplacementsLibraryListItem>) => {
      set(state => ({
        library: library.map<ReplacementLibraryStoreItem>(item => {
          const existingItem = state.library.find(i => i.text === item.text);
          return {
            ...item,
            expanded: existingItem?.expanded ?? false,
            selected: existingItem?.selected ?? false,
            selectedReplacement: existingItem?.selectedReplacement,
            hasReplacementEnabled: existingItem?.hasReplacementEnabled,
          };
        }),
      }));
    },
    setActiveType: (activeType: ExtendedPhoneticLibraryType) => {
      set(() => {
        return { activeType };
      });
    },
    selectWord: (text: string, deselect?: boolean) => {
      set(state => {
        const { library } = state;

        // deselect word
        if (deselect) {
          const updatedLibrary = deepCopyArray(library).map(item => ({
            ...item,
            selected: false,
          }));
          return { library: updatedLibrary };
        }
        // select word
        const updatedLibrary = deepCopyArray(library).map(item => ({
          ...item,
          selected: item.text === text,
        }));
        return { library: updatedLibrary };
      });
    },
    expandWord: (text: string, expanded: boolean) => {
      set(state => {
        const { library } = state;

        const updatedLibrary = deepCopyArray(library);
        const index = updatedLibrary.findIndex(item => item.text === text);
        if (index !== -1) {
          updatedLibrary[index].expanded = expanded;
        }
        return { library: updatedLibrary };
      });
    },
    addWord: (phoneticReplacement: PhoneticReplacement) => {
      set(state => {
        const { library } = state;
        if (library.some(item => item.text === phoneticReplacement.text)) {
          return state;
        }
        const updatedLibrary = insertToLibrary(library, phoneticReplacement);

        return { library: updatedLibrary };
      });
    },
    addReplacement: (phoneticReplacement: PhoneticReplacement, context) => {
      set(state => {
        const { library } = state;
        const index = library.findIndex(
          item => item.text === phoneticReplacement.text
        );

        // original word in library; update word replacement array
        if (index !== -1) {
          if (
            library[index].replacements.some(
              r => r.id === phoneticReplacement.id
            )
          ) {
            return state;
          }
          const updatedLibrary = deepCopyArray(library);
          updatedLibrary[index].replacements.unshift({
            id: phoneticReplacement.id,
            text: phoneticReplacement.text,
            replacement: phoneticReplacement.replacement,
            enabled: true,
            isRespelling: phoneticReplacement.isRespelling,
            userId: phoneticReplacement.userId,
            updatedAt: phoneticReplacement.updatedAt,
          });
          return { library: updatedLibrary };
        }
        // original word not in library; add word
        const updatedLibrary = insertToLibrary(
          library,
          phoneticReplacement,
          context
        );
        return { library: updatedLibrary };
      });
    },
    selectReplacement: (text: string, replacement: string) => {
      set(state => {
        const { library } = state;

        const updatedLibrary = deepCopyArray(library).map(item => ({
          ...item,
          selectedReplacement: item.text === text ? replacement : undefined,
        }));

        return { library: updatedLibrary };
      });
    },
    clearSelectedReplacement: () => {
      set(state => {
        const { library } = state;

        const updatedLibrary = deepCopyArray(library).map(item => ({
          ...item,
          selectedReplacement: undefined,
        }));

        return { library: updatedLibrary };
      });
    },
    getSelectedWord: () => {
      const { library } = get();
      const selectedItem = library.find(item => item.selected === true);
      return selectedItem?.text ?? '';
    },
    getSelectedReplacement: () => {
      const { library } = get();
      const selectedItem = library.find(item => item.selected === true);
      const selectedReplacement = selectedItem?.replacements.find(
        rep => rep.replacement === selectedItem?.selectedReplacement
      );
      return selectedReplacement;
    },
    updateDrawerContentType: (type: LibraryToolbarDrawerContentType) => {
      set({ drawerContentType: type });
    },
    deleteReplacement: (text: string, replacementId: string) => {
      let nextState = LibraryToolbarDrawerContentType.Default;
      set(state => {
        const { library } = state;

        const updatedLibrary = deepCopyArray(library);
        const selectedWordIndex = updatedLibrary.findIndex(
          item => item.text === text
        );
        if (selectedWordIndex >= 0) {
          const selectedWord = updatedLibrary[selectedWordIndex];
          selectedWord.replacements = selectedWord.replacements.filter(
            item => item.id !== replacementId
          );
          if (selectedWord.replacements.length === 0) {
            nextState = LibraryToolbarDrawerContentType.Default;
            return {
              library: updatedLibrary.filter(item => item.text !== text),
            };
          }
          nextState = LibraryToolbarDrawerContentType.WordSelected;
        }
        return { library: updatedLibrary };
      });
      return nextState;
    },
    updateReplacement: (
      phoneticReplacement: PhoneticReplacement,
      selectReplacement: boolean = false
    ) => {
      set(state => {
        const { library } = state;
        const updatedLibrary = deepCopyArray(library);
        const selectedWordIndex = updatedLibrary.findIndex(
          item => item.text === phoneticReplacement.text
        );
        if (selectedWordIndex >= 0) {
          const selectedWord = updatedLibrary[selectedWordIndex];
          if (selectReplacement)
            selectedWord.selectedReplacement = phoneticReplacement.replacement;

          selectedWord.replacements = selectedWord.replacements.map(item => {
            if (item.id === phoneticReplacement.id) {
              return {
                ...item,
                ...phoneticReplacement,
              };
            }
            return item;
          });
        }
        return { library: updatedLibrary };
      });
    },
    deleteWord: (text: string) => {
      set(state => {
        const { library } = state;

        const updatedLibrary = deepCopyArray(library).filter(
          item => item.text !== text
        );

        return { library: updatedLibrary };
      });
    },
    collapseWords: () => {
      set(state => {
        const { library } = state;

        const updatedLibrary = deepCopyArray(library).map(item => ({
          ...item,
          expanded: false,
        }));
        return { library: updatedLibrary };
      });
    },
  })
);
